import { useState } from "react";
import { message } from "antd";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import cloneDeep from "lodash.clonedeep";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { programAutomationActions } from "@fitness-app/app-store";
import {
  TemplateType,
  type OneTimeSurveyTemplate,
  type RecurringSurveyTemplate,
} from "@fitness-app/data-models/entities/AutomationTemplate";
import { CycleSurveyType, RecurringSurveyStatus, SurveyType } from "@fitness-app/data-models/entities/ClientSurvey";
import {
  type ProgramAutomationScheduledTask,
  type WeekdayNumberToString,
} from "@fitness-app/data-models/entities/ProgramAutomation";
import { type SurveyTemplate } from "@fitness-app/data-models/entities/SurveyTemplate";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import { type AddSurveyTemplateFormModel } from "~/modules/Automation/ProgramAutomation/components/AddSurveyTemplateForm/types";
import { isPackageAnchoredTiming } from "~/modules/Automation/ProgramAutomation/components/ScheduleTimingFields/scheduleTimingLabel";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { transformFromFormDataToFilters } from "../FiltersField/FiltersField";

dayjs.extend(timezone);

const generateTemplate = (template: SurveyTemplate) => {
  return {
    id: generateUniqId(),
    name: template.name,
    description: template.description,
    templateId: template.id,
    questions: template.questions,
    automation: template.automation || null,
    answers: null,
    notRemovable: false,
  };
};

const getVariables = (model: AddSurveyTemplateFormModel) => {
  if (!model.variables) {
    return null;
  }
  return Object.fromEntries(Object.entries(model.variables).map(([key, value]) => [key, value || ""]));
};

export const useSurveyTemplateForm = (
  selected: null | {
    week: string;
    day: WeekdayNumberToString;
  },
  setCell: () => void,
) => {
  const { selectedAutomation } = useAppSelector((store) => store.programAutomation);
  const [showSurveyForm, toggleSurveyForm] = useState(false);
  const dispatch = useAppDispatch();
  const [addingSurvey, toggleStatus] = useState(false);
  const { t } = useTranslation(["automation", "common"]);

  const handleFormSubmit = async (formModel: AddSurveyTemplateFormModel, template: SurveyTemplate) => {
    let surveyToAdd: ProgramAutomationScheduledTask | null = null;
    toggleStatus(true);

    const timezone = dayjs.tz.guess() || "Europe/Warsaw";

    if (formModel.type === SurveyType.OneTime) {
      const clientOneTimeSurvey: OneTimeSurveyTemplate = {
        ...generateTemplate(template),
        variables: getVariables(formModel),
        title: formModel.title,
        type: SurveyType.OneTime,
        editable: formModel.editable,
        interval: null,
      };

      surveyToAdd = {
        type: TemplateType.ScheduledSurvey,
        survey: clientOneTimeSurvey,
        id: generateUniqId(),
        name: formModel.title,
        templateId: null,
        comment: "",
        filters: transformFromFormDataToFilters(formModel.filters),
        filterOperator: formModel.filterOperator,
        sentTime: {
          hours: formModel.timeOfDelivery.get("hour"),
          minutes: formModel.timeOfDelivery.get("minutes"),
          offset: formModel.timeOfDelivery.utcOffset(),
          timezone,
          mode: formModel.sentTimeConfig?.mode ?? "absolute",
          avoidSendingOnTheNight: formModel.sentTimeConfig?.avoidSendingOnTheNight ?? true,
          calculateFullRangeOverNight: formModel.sentTimeConfig?.calculateFullRangeOverNight ?? false,
        },
      };
    } else {
      if (formModel.cycleType === CycleSurveyType.Infinite) {
        const infiniteCycle: RecurringSurveyTemplate = {
          ...generateTemplate(template),
          variables: getVariables(formModel),
          title: formModel.title,
          type: SurveyType.Recurring,
          editable: formModel.editable,
          interval: {
            dayOfDelivery: formModel.dayOfDelivery || "monday",
            status: RecurringSurveyStatus.New,
            count: formModel.count || 2,
            unit: formModel.unit,
            timeOfDelivery: formModel.timeOfDelivery
              ? [
                  formModel.timeOfDelivery.get("hour"),
                  formModel.timeOfDelivery.get("minutes"),
                  formModel.timeOfDelivery.utcOffset(),
                  timezone,
                ]
              : [0, 0, 0],
            cycle: {
              type: CycleSurveyType.Infinite,
            },
          },
          cycleNumber: 1,
        };

        surveyToAdd = {
          id: generateUniqId(),
          name: formModel.title,
          templateId: null,
          comment: "",
          type: TemplateType.ScheduledSurvey,
          survey: infiniteCycle,
          sentTime: {
            hours: formModel.timeOfDelivery.get("hour"),
            minutes: formModel.timeOfDelivery.get("minutes"),
            offset: formModel.timeOfDelivery.utcOffset(),
            timezone,
            mode: formModel.sentTimeConfig?.mode ?? "absolute",
            avoidSendingOnTheNight: formModel.sentTimeConfig?.avoidSendingOnTheNight ?? true,
            calculateFullRangeOverNight: formModel.sentTimeConfig?.calculateFullRangeOverNight ?? false,
          },
          filters: transformFromFormDataToFilters(formModel.filters),
          filterOperator: formModel.filterOperator,
        };
      }

      if (formModel.cycleType === CycleSurveyType.CycleNumber) {
        const infiniteCycle: RecurringSurveyTemplate = {
          ...generateTemplate(template),
          variables: getVariables(formModel),
          title: formModel.title,
          type: SurveyType.Recurring,
          editable: formModel.editable,
          interval: {
            dayOfDelivery: formModel.dayOfDelivery || "monday",
            status: RecurringSurveyStatus.New,
            count: formModel.count || 2,
            unit: formModel.unit,
            timeOfDelivery: formModel.timeOfDelivery
              ? [
                  formModel.timeOfDelivery.get("hour"),
                  formModel.timeOfDelivery.get("minutes"),
                  formModel.timeOfDelivery.utcOffset(),
                  timezone,
                ]
              : [0, 0, 0, timezone],
            cycle: {
              type: CycleSurveyType.CycleNumber,
              numberOfCycles: formModel.cycleTypeNumber || 1,
            },
          },
          cycleNumber: 1,
        };

        surveyToAdd = {
          id: generateUniqId(),
          name: formModel.title,
          templateId: null,
          comment: "",
          type: TemplateType.ScheduledSurvey,
          survey: infiniteCycle,
          filters: transformFromFormDataToFilters(formModel.filters),
          filterOperator: formModel.filterOperator,
          sentTime: {
            hours: formModel.timeOfDelivery.get("hour"),
            minutes: formModel.timeOfDelivery.get("minutes"),
            offset: formModel.timeOfDelivery.utcOffset(),
            timezone,
            mode: formModel.sentTimeConfig?.mode ?? "absolute",
            avoidSendingOnTheNight: formModel.sentTimeConfig?.avoidSendingOnTheNight ?? true,
            calculateFullRangeOverNight: formModel.sentTimeConfig?.calculateFullRangeOverNight ?? false,
          },
        };
      }

      if (formModel.cycleType === CycleSurveyType.UntilDate) {
        const infiniteCycle: RecurringSurveyTemplate = {
          ...generateTemplate(template),
          variables: getVariables(formModel),
          title: formModel.title,
          type: SurveyType.Recurring,
          editable: formModel.editable,
          interval: {
            dayOfDelivery: formModel.dayOfDelivery || "monday",
            status: RecurringSurveyStatus.New,
            count: formModel.count || 2,
            unit: formModel.unit,
            timeOfDelivery: formModel.timeOfDelivery
              ? [
                  formModel.timeOfDelivery.get("hour"),
                  formModel.timeOfDelivery.get("minutes"),
                  formModel.timeOfDelivery.utcOffset(),
                  timezone,
                ]
              : [0, 0, 0],
            cycle: {
              type: CycleSurveyType.UntilDate,
              date: formModel.untilDate ? formModel.untilDate.toISOString() : dayjs().add(1, "month").toISOString(),
            },
          },
          cycleNumber: 1,
        };

        surveyToAdd = {
          id: generateUniqId(),
          name: formModel.title,
          templateId: null,
          comment: "",
          type: TemplateType.ScheduledSurvey,
          survey: infiniteCycle,
          filters: transformFromFormDataToFilters(formModel.filters),
          filterOperator: formModel.filterOperator,
          sentTime: {
            hours: formModel.timeOfDelivery.get("hour"),
            minutes: formModel.timeOfDelivery.get("minutes"),
            offset: formModel.timeOfDelivery.utcOffset(),
            timezone,
            mode: formModel.sentTimeConfig?.mode ?? "absolute",
            avoidSendingOnTheNight: formModel.sentTimeConfig?.avoidSendingOnTheNight ?? true,
            calculateFullRangeOverNight: formModel.sentTimeConfig?.calculateFullRangeOverNight ?? false,
          },
        };
      }
    }

    if (surveyToAdd) {
      if (!selectedAutomation) {
        throw new Error("No selected automation");
      }
      surveyToAdd.scheduleTiming = formModel.scheduleTiming ?? null;

      let automationUpdate: Partial<typeof selectedAutomation>;

      if (isPackageAnchoredTiming(surveyToAdd.scheduleTiming)) {
        // package-anchored surveys live in their own bag, outside the week/day grid
        automationUpdate = {
          id: selectedAutomation.id,
          packageAnchoredTasks: [...(selectedAutomation.packageAnchoredTasks ?? []), surveyToAdd],
        };
      } else {
        // added from the package-tasks card without a source cell -> week 1 / day 1
        const cell = selected ?? { week: "1", day: "1" as const };
        const weekToUpdate = cloneDeep(selectedAutomation.schedule[cell.week]) || {};
        const itemsToUpdate = weekToUpdate[cell.day];
        weekToUpdate[cell.day] = {
          id: weekToUpdate[cell.day]?.id ?? uuid(),
          items: itemsToUpdate?.items ? [...itemsToUpdate.items, surveyToAdd] : [surveyToAdd],
        };
        automationUpdate = {
          id: selectedAutomation.id,
          schedule: {
            ...selectedAutomation.schedule,
            [cell.week]: weekToUpdate,
          },
        };
      }

      try {
        await dispatch(
          programAutomationActions.updateAutomationTemplate({
            automationId: selectedAutomation.id,
            automation: automationUpdate,
          }),
        ).unwrap();
        toggleSurveyForm(false);
        setCell();
      } catch (e) {
        void message.error(t("saveError"));
      } finally {
        toggleStatus(false);
      }
    }
  };

  return {
    onSendSurveyForm: handleFormSubmit,
    showSurveyForm,
    toggleSurveyForm,
    addingSurvey,
  };
};
